import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {always, isNil} from 'ramda';

import {
  accountingRoutes,
  businessCaseRoutes,
  customerRoutes,
  dashboardRoutes,
  employeeRoutes,
  interestRoutes,
  rentalOrderRoutes,
  tiresInventoryRoutes,
  vehiclesRoutes,
  warehouseRoutes,
  workshopRoutes,
} from '@dms/routes';

import {composePath} from 'shared/web';

type EntityResource =
  | 'BUSINESS_CASE'
  | 'VEHICLE'
  | 'EMPLOYEE'
  | 'INVOICE'
  | 'CASH_RECEIPT'
  | 'BALANCE_INVOICE'
  | 'INVOICE_PROFORMA'
  | 'CASH_REGISTER_DOCUMENT'
  | 'WAREHOUSE_ISSUE_NOTE_DETAIL'
  | 'WAREHOUSE_ISSUE_NOTE_CORRECTION_DETAIL'
  | 'WAREHOUSE_RECEIVE_NOTE_DETAIL'
  | 'WAREHOUSE_RECEIVE_NOTE_CORRECTION_DETAIL'
  | 'WAREHOUSE_DIRECT_SALE_DETAIL'
  | 'WAREHOUSE_SERVICE_ORDER_ISSUE_NOTE_DETAIL'
  | 'WAREHOUSE_ISSUE_NOTE_VALUE_CORRECTION_DETAIL'
  | 'WORKSHOP_SERVICE_ORDER_DETAIL'
  | 'WAREHOUSE_FAST_TRACK_DETAILS'
  | 'WAREHOUSE_DELIVERY_NOTE_DETAIL'
  | 'WAREHOUSE_SERVICE_ORDER_RETURN_DETAIL'
  | 'WAREHOUSE_SUPPLIER_ORDER_DETAIL'
  | 'WAREHOUSE_TRANSFER_ORDER_DETAIL'
  | 'WAREHOUSE_UPDATE_JOB_DETAIL'
  | 'WAREHOUSE_ARTICLE_DETAIL'
  | 'CUSTOMER'
  | 'INTEREST'
  | 'SERVICE_ORDER'
  | 'SERVICE_CASE'
  | 'TIRE_ORDER'
  | 'DIRECT_SALE'
  | 'SERVICE_ORDER_ISSUE'
  | 'RENTAL_ORDER'
  | 'WORKSHOP_INTEREST';

/**
 * @about This is a function that returns a string, which is a path to a resource. It is used in the DataGrid Link renderer.
 *
 * @example entityResourceResolver('VEHICLE', '123') // => '/vehicles/123'
 * @example entityResourceResolver('BUSINESS_CASE', '123') // => '/business-cases/123'
 */
export const entityResourceResolver = (
  entityResource: string,
  id: string,
  additionalParams?: Record<string, string>
): string =>
  match(entityResource as EntityResource)
    .with(
      'BUSINESS_CASE',
      always(
        composePath(businessCaseRoutes.overview, {params: {id}, isSearchParamsPreserved: false})
      )
    )
    .with(
      'VEHICLE',
      always(composePath(vehiclesRoutes.detail, {params: {id}, isSearchParamsPreserved: false}))
    )
    .with(
      'EMPLOYEE',
      always(composePath(employeeRoutes.detail, {params: {id}, isSearchParamsPreserved: false}))
    )
    .with(
      'CUSTOMER',
      always(composePath(customerRoutes.detail, {params: {id}, isSearchParamsPreserved: false}))
    )
    .with(
      'INVOICE',
      always(
        composePath(accountingRoutes.invoiceDetail, {params: {id}, isSearchParamsPreserved: false})
      )
    )
    .with(
      'CASH_RECEIPT',
      always(
        composePath(accountingRoutes.cashReceiptDetail, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'INVOICE_PROFORMA',
      always(
        composePath(accountingRoutes.proformaInvoiceDetail, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'CASH_REGISTER_DOCUMENT',
      always(
        composePath(accountingRoutes.cashReceiptDetail, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'BALANCE_INVOICE',
      always(
        composePath(accountingRoutes.balanceInvoiceDetail, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_ISSUE_NOTE_DETAIL',
      always(
        composePath(warehouseRoutes.issueNoteDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_ISSUE_NOTE_CORRECTION_DETAIL',
      always(
        composePath(warehouseRoutes.issueNoteCorrectionDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_RECEIVE_NOTE_DETAIL',
      always(
        composePath(warehouseRoutes.receiveNoteDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_RECEIVE_NOTE_CORRECTION_DETAIL',
      always(
        composePath(warehouseRoutes.receiveNoteCorrectionDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_DIRECT_SALE_DETAIL',
      always(
        composePath(warehouseRoutes.directSalesDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_SERVICE_ORDER_ISSUE_NOTE_DETAIL',
      always(
        composePath(warehouseRoutes.serviceOrderIssueDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_ISSUE_NOTE_VALUE_CORRECTION_DETAIL',
      always(
        composePath(warehouseRoutes.costCorrectionDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_FAST_TRACK_DETAILS',
      always(
        composePath(warehouseRoutes.fastTrackIssuesDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    // TODO https://carvago.atlassian.net/browse/T20-68079
    .with(
      'WORKSHOP_SERVICE_ORDER_DETAIL',
      always(
        composePath(workshopRoutes.serviceCaseDetail, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'INTEREST',
      always(
        composePath(interestRoutes.detailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with('SERVICE_ORDER', () => {
      if (isNil(additionalParams?.serviceCaseId)) {
        showNotification.error('Service case ID is required');
        return dashboardRoutes.home;
      }

      return composePath(workshopRoutes.serviceCaseDetail, {
        params: {id: additionalParams?.serviceCaseId},
        isSearchParamsPreserved: false,
        queryParams: {
          section: 'orders',
          orderId: id,
        },
      });
    })
    .with('SERVICE_CASE', () =>
      composePath(workshopRoutes.serviceCaseDetail, {
        params: {id},
        isSearchParamsPreserved: false,
      })
    )
    .with('TIRE_ORDER', () =>
      composePath(tiresInventoryRoutes.tireOrderDetail, {
        params: {id},
        isSearchParamsPreserved: false,
      })
    )
    .with('DIRECT_SALE', () =>
      composePath(warehouseRoutes.directSalesDetailOverview, {
        params: {id},
        isSearchParamsPreserved: false,
      })
    )
    .with('SERVICE_ORDER_ISSUE', () =>
      composePath(warehouseRoutes.serviceOrderIssueDetailOverview, {
        params: {id},
        isSearchParamsPreserved: false,
      })
    )
    .with(
      'WAREHOUSE_DELIVERY_NOTE_DETAIL',
      always(
        composePath(warehouseRoutes.deliveryNoteDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_SERVICE_ORDER_RETURN_DETAIL',
      always(
        composePath(warehouseRoutes.serviceOrderReturnsDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_SUPPLIER_ORDER_DETAIL',
      always(
        composePath(warehouseRoutes.supplierOrderDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_TRANSFER_ORDER_DETAIL',
      always(
        composePath(warehouseRoutes.transferOrderDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'WAREHOUSE_UPDATE_JOB_DETAIL',
      always(
        composePath(warehouseRoutes.updateJobDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with(
      'RENTAL_ORDER',
      always(
        composePath(rentalOrderRoutes.vehicleAndCustomer, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .with('WAREHOUSE_ARTICLE_DETAIL', () => {
      if (isNil(additionalParams?.warehouseId)) {
        showNotification.error('Warehouse ID is required');
        return dashboardRoutes.home;
      }

      return composePath(warehouseRoutes.articleDetailOverview, {
        params: {warehouseId: additionalParams?.warehouseId, id},
        isSearchParamsPreserved: false,
      });
    })
    .with(
      'WORKSHOP_INTEREST',
      always(
        composePath(workshopRoutes.interestDetailOverview, {
          params: {id},
          isSearchParamsPreserved: false,
        })
      )
    )
    .exhaustive();
